// Kit status + delivery (issue #10): live pipeline status, artifact downloads
// (Storage signed-URL pattern — paths in Firestore, URLs generated on demand),
// and the per-kit print instructions (2-up, flip-on-short-edge).
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { artifactUrl, createKit, isMock, THEME_CATALOG, watchKit, watchVariantsOf } from "../api";
import type { Kit } from "../types";
import { track, EVENTS } from "../analytics";

const KIND_LABEL: Record<string, string> = {
  flashcards: "Flashcards (cut-in-one-pass grid)",
  wordsearch: "Word Search + answer key",
  crossword: "Crossword + clues + answer key",
  handwriting: "Handwriting tracing sheet",
  booklet: "Storybook (print 2-up, flip on short edge)",
};
// #39 "generate another": re-rollable printables only — flashcards and
// handwriting are deterministic reprints (server skips them even if asked).
const REGEN_KINDS = ["wordsearch", "crossword", "booklet"] as const;
const KIND_SHORT: Record<string, string> = { wordsearch: "word search", crossword: "crossword", booklet: "booklet" };

export default function KitPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [kit, setKit] = useState<Kit | null>(null);
  const readySent = useRef(false);
  const [celebrate, setCelebrate] = useState(false);
  // #39 "generate another": variant shelf + multi-select panel state.
  const [variants, setVariants] = useState<Kit[]>([]);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenSel, setRegenSel] = useState<string[]>([]);
  const [regenTheme, setRegenTheme] = useState("");
  const [regenFormat, setRegenFormat] = useState<"saddle" | "flat">("saddle");
  const [regenErr, setRegenErr] = useState("");
  const [regenBusy, setRegenBusy] = useState(false);

  useEffect(() => (id ? watchKit(id, (k) => {
    setKit(k);
    if (k && k.status === "ready" && !readySent.current) {
      readySent.current = true;
      setCelebrate(true);
      track(EVENTS.kitReady, { word_count: k.wordCount ?? 0, artifact_count: k.artifacts.length });
      // #34 Phase 2 (web): notify when the kit flips to ready so parents can
      // leave the tab. Permission requested lazily on first ready kit.
      if ("Notification" in window && Notification.permission === "default") {
        void Notification.requestPermission();
      } else if ("Notification" in window && Notification.permission === "granted") {
        new Notification(`Kit ready: ${k.title}`, { body: "Your printables are ready to download." });
      }
    }
  }) : () => {}), [id]);

  // #39: subscribe to this module's variants once the root kit is known.
  useEffect(() => {
    if (!kit || kit.regen) return () => {};
    return watchVariantsOf(kit.kidId, kit.id, setVariants);
  }, [kit?.id, kit?.kidId, kit?.regen]);

  async function startRegen() {
    if (!kit || regenSel.length === 0 || regenBusy) return;
    setRegenBusy(true);
    setRegenErr("");
    try {
      const newId = await createKit(kit.kidId, kit.extraction ?? null, regenTheme || kit.theme || "",
        regenFormat || kit.bookletFormat || "saddle", undefined, kit.id, regenSel);
      track("generate_another", { kinds: regenSel.join(","), theme: regenTheme });
      nav(`/kit/${newId}`);
    } catch (e) {
      const err = e as { code?: string; message?: string };
      // Quota denials (free = one kit/rolling week) get the paywall copy,
      // not the raw error string.
      if (err.code === "permission-denied" && /upgrade|plan/i.test(err.message ?? "")) {
        setRegenErr(err.message ?? "Plan limit reached.");
        track(EVENTS.paywallSeen ?? "paywall_seen", { from: "generate_another" });
      } else {
        setRegenErr(String(err.message ?? e));
      }
      setRegenBusy(false);
    }
  }

  if (!kit) return <p className="muted">Loading kit…</p>;

  // #34 Phase 1 parity: stage labels + honest duration + permission to leave.
  const stage = kit.status === "queued" ? "Queued — starting soon…"
    : kit.status === "building" ? "Building your printables…"
    : kit.status === "validating" ? "Almost done — checking quality…" : null;

  return (
    <>
      <h1>{kit.title} <span className={`status-pill status-${kit.status}`}>{kit.status}</span></h1>
      {stage && (
        <p role="status" aria-live="polite" className="muted">
          <span className="spinner" aria-hidden="true" />{stage}{" "}
          Usually under two minutes — feel free to close this tab, the kit will be waiting on your home list.
        </p>
      )}
      <p className="muted">{[kit.week, `${kit.wordCount} words`].filter(Boolean).join(" · ")}</p>

      {/* "Another copy of this module" (Thomas UX ask): reprint the same word
          list with a new theme/print style/kid — no re-shooting. Kits persist
          their extraction; older kits without one fall back to a fresh wizard. */}
      {(kit.status === "ready" || kit.extraction) && (
        <p>
          <button className="ghost"
            onClick={() => nav(kit.extraction ? `/new?copyFrom=${kit.id}` : "/new")}>
            Make another copy of this module
          </button>
        </p>
      )}

      {/* #39: variants link back to the module they re-rolled from. */}
      {kit.regen && (
        <p>
          <button className="ghost" onClick={() => nav(`/kit/${kit.regen!.of}`)}>
            ← Back to the original module
          </button>
        </p>
      )}

      {/* #39 "generate another": pick printables to re-roll — same word list,
          fresh puzzles; the current copies stay printable. Root kits only. */}
      {kit.status === "ready" && !kit.regen && (
        regenOpen ? (
          <div className="card" role="group" aria-label="Generate another">
            <strong>Generate another</strong>
            <p className="muted">Same word list, fresh printables — the current copies stay printable. Pick what to make and set the scene:</p>
            {REGEN_KINDS.map((k) => (
              <label key={k} style={{ display: "block", margin: "6px 0" }}>
                <input type="checkbox" checked={regenSel.includes(k)}
                  onChange={(e) => setRegenSel((s) => e.target.checked ? [...s, k] : s.filter((x) => x !== k))} />{" "}
                {KIND_LABEL[k]}
              </label>
            ))}
            {/* Thomas: regen = same words, NEW story — parent re-picks theme/format (pre-filled from source). */}
            <label style={{ display: "block", margin: "8px 0 4px" }}>Theme
              <select value={regenTheme} onChange={(e) => setRegenTheme(e.target.value)}
                style={{ display: "block", marginTop: 4, minWidth: 260 }}>
                {THEME_CATALOG.map((t) => <option key={t.slug} value={t.slug}>{t.name} — {t.world}</option>)}
              </select>
            </label>
            <label style={{ display: "block", margin: "8px 0 4px" }}>Booklet style
              <select value={regenFormat} onChange={(e) => setRegenFormat(e.target.value as "saddle" | "flat")}
                style={{ display: "block", marginTop: 4, minWidth: 260 }}>
                <option value="saddle">Folded booklet — 4 sheets, stapled (16 pages)</option>
                <option value="flat">Flat pages — 1 per sheet, no folding (16 sheets)</option>
              </select>
            </label>
            {regenErr && <p className="muted" style={{ color: "var(--danger-ink, #b3261e)" }}>{regenErr}</p>}
            <p className="muted" style={{ fontSize: ".85rem" }}>Counts against your plan like a new kit.</p>
            <p style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button className="primary" disabled={regenSel.length === 0 || regenBusy} onClick={startRegen}>
                {regenBusy ? "Starting…" : "Generate"}
              </button>
              <button className="ghost" onClick={() => setRegenOpen(false)}>Cancel</button>
            </p>
          </div>
        ) : (
          <p>
            <button className="ghost"
              onClick={() => {
                setRegenSel(kit.artifacts.map((a) => a.kind).filter((k) => (REGEN_KINDS as readonly string[]).includes(k)));
                setRegenTheme(kit.theme || THEME_CATALOG[0].slug);
                setRegenFormat(kit.bookletFormat === "flat" ? "flat" : "saddle");
                setRegenOpen(true);
              }}>
              Generate another… (fresh puzzle/book)
            </button>
          </p>
        )
      )}

      {kit.status === "failed" && (
        <div className="card err">Generation failed validation: {kit.error ?? "see logs"}. No partial kit ships — the validators caught it.</div>
      )}

      {/* The "aha" moment (mobile PopIn mirror): shown once per kit per page
          session when the kit reaches ready. */}
      {celebrate && kit.artifacts.length > 0 && (
        <div className="card ok celebrate" role="status">
          <span aria-hidden="true" style={{ fontSize: "1.6rem" }}>🎉</span>{" "}
          <strong>{kit.wordCount ?? 0} words ready to print!</strong>{" "}
          <span className="muted">Print at home and start the week.</span>
        </div>
      )}

      {(kit.status === "ready" || kit.status === "validating") && kit.artifacts.length > 0 && (
        /* Collapsed behind a summary line (Thomas UX pass): most parents print
           once and never re-read the instructions. <details> is keyboard- and
           screen-reader-accessible for free. */
        <details className="card print-help">
          <summary>
            <strong>Print instructions</strong>{" "}
            <span className="muted">
              {/* #41: legacy kits lack per-artifact pages — never show a wrong 0 */}
              {(() => {
                const sheets = kit.artifacts.reduce((n, a) => n + (a.pages ?? 0), 0);
                return sheets > 0
                  ? `${sheets} sheets across ${kit.artifacts.length} PDFs`
                  : `${kit.artifacts.length} PDFs`;
              })()}
            </span>
          </summary>
          <ul>
            {/* #35: instructions adapt to the chosen booklet format */}
            {kit.bookletFormat === "flat" ? (
              <li>Booklet (flat): 16 portrait pages, one per sheet. Print single-sided in page order; hole-punch or ready-folder. No folding or stapling.</li>
            ) : (
              <li>Booklet: duplex, <strong>flip on short edge</strong>, then fold each sheet in half and staple the two spine marks. 4 sheets → 16 book pages.</li>
            )}
            <li>Letter size, 100% scale (no fit-to-page). Puzzles/flashcards print portrait; the saddle booklet sheet is landscape (2-up imposition with dashed fold line).</li>
            <li>Flashcards: cut on the single outer border only — cards are already edge-to-edge.</li>
            <li>Ink-saver: booklet art pages print fine in grayscale (Draft/lower quality on most drivers); puzzles and flashcards are already low-ink line art.</li>
          </ul>
        </details>
      )}

      <ul className="artifacts">
        {kit.artifacts.map((a) => (
          <li key={a.kind}>
            <span>
              {a.kind === "booklet" && kit.bookletFormat === "flat"
                ? "Storybook (flat — one page per sheet)"
                : (KIND_LABEL[a.kind] ?? a.kind)}{" "}
              {/* #35: page-count footprint per artifact */}
              {!!a.pages && <span className="muted">· {a.pages} sheet{a.pages === 1 ? "" : "s"}{a.kind === "booklet" && kit.bookletFormat !== "flat" ? ", folded to 16 pages" : ""}</span>}
            </span>
            {/* #39: new-version affordance on the printable itself (root only).
                Plus, not ↻: regen ADDS a variant, never overwrites (Thomas UX note). */}
            {kit.status === "ready" && !kit.regen && (REGEN_KINDS as readonly string[]).includes(a.kind) && (
              <button className="ghost" aria-label={`Make a new ${KIND_SHORT[a.kind] ?? a.kind} from this kit`}
                onClick={() => { setRegenSel([a.kind]); setRegenOpen(true); }}>
                + New version
              </button>
            )}
            <ArtifactLink a={a} />
          </li>
        ))}
      </ul>

      {/* #39: re-rolls of this module live under the originals. */}
      {!kit.regen && variants.length > 0 && (
        <>
          <h2>Other versions of this module <span className="muted">{variants.length}</span></h2>
          <ul className="artifacts">
            {[...variants].sort((a, b) => a.createdAt - b.createdAt).map((v, i) => (
              <li key={v.id}>
                <span>
                  Version {i + 1} — {v.regen?.kinds.map((k) => KIND_SHORT[k] ?? k).join(", ")}{" "}
                  {v.status === "ready"
                    ? <span className="muted">· {v.artifacts.reduce((n, a) => n + (a.pages ?? 0), 0)} sheet{v.artifacts.reduce((n, a) => n + (a.pages ?? 0), 0) === 1 ? "" : "s"}</span>
                    : <span className="muted">· {v.status}</span>}
                </span>
                {v.status === "ready" && v.artifacts.map((a) => <ArtifactLink key={a.kind} a={a} />)}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

function ArtifactLink({ a }: { a: Kit["artifacts"][number] }) {
  const [url, setUrl] = useState("");
  const [err, setErr] = useState("");
  return (
    <button
      className="ghost"
      onClick={async () => {
        setErr("");
        try {
          const u = await artifactUrl(a);
          setUrl(u);
          track(EVENTS.artifactDownload, { kind: a.kind });
        }
        catch (e) { setErr(String((e as { message?: string }).message ?? e)); }
      }}
    >
      {url ? <a href={url} target="_blank" rel="noreferrer">Open PDF</a>
        : err || (isMock ? "mock: no PDF stored" : "Get download link")}
    </button>
  );
}
